import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { toast } from '@/shared/lib/toast';
import { projectsService } from '../api/projects';
import { cacheInvalidation, cacheKeys } from '@/shared/lib/cache-invalidation';
import { goalsAutosaveCoordinator } from '@/features/project/lib/goals-autosave-coordinator';
import type { UpdateProjectRequest } from '@/types/project';

// Types for query params
interface ProjectListParams {
  sort?: string;
  filter?: Record<string, unknown>;
  enabled?: boolean;
}

// Hook for fetching all projects
export function useProjects(params?: ProjectListParams) {
  return useQuery({
    queryKey: cacheKeys.projects.list(params),
    queryFn: () =>
      projectsService.list({
        sort: params?.sort || '-created_at',
        filter: params?.filter,
      }),
    enabled: params?.enabled !== false,
  });
}

// Hook for fetching a single project
export function useProject(projectId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: cacheKeys.projects.detail(projectId),
    // `signal` ties the fetch to the screen that asked for it — see the same
    // pattern on the concept detail query.
    queryFn: ({ signal }) => projectsService.get(projectId, { signal }),
    enabled: !!projectId && options?.enabled !== false,
  });
}

// Hook for creating a project
export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['projects', 'create'],
    mutationFn: (name: string) => projectsService.create(name),
    onSuccess: (response) => {
      // Invalidate and refetch projects list
      queryClient.invalidateQueries({ queryKey: cacheKeys.projects.lists() });

      // Cache invalidation for other features
      cacheInvalidation.afterCreateProject();

      return response.data;
    },
  });
}

// Hook for updating a project
export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['projects', 'update'],
    meta: { lockId: (vars: unknown) => (vars as { projectId: string }).projectId },
    mutationFn: ({
      projectId,
      attributes,
    }: {
      projectId: string;
      // The WRITE shape, not the read shape: some fields differ between them
      // (e.g. `building_stage` reads back as an object with label/options but
      // is written as a plain value).
      attributes: Partial<UpdateProjectRequest['data']['attributes']>;
    }) => projectsService.update(projectId, attributes),
    onSuccess: (response, variables) => {
      // Update the specific project in cache
      queryClient.setQueryData(
        cacheKeys.projects.detail(variables.projectId),
        response
      );

      // Invalidate lists to ensure consistency
      queryClient.invalidateQueries({ queryKey: cacheKeys.projects.lists() });

      // Cache invalidation for other features
      cacheInvalidation.afterUpdateProject(variables.projectId);

      return response.data;
    },
  });
}

// Hook for deleting a project
export function useDeleteProject() {
  const queryClient = useQueryClient();
  const t = useTranslations('project');
  const tCommon = useTranslations('common');

  return useMutation({
    mutationKey: ['projects', 'delete'],
    meta: { lockId: (vars: unknown) => vars as string },
    mutationFn: (projectId: string) => projectsService.delete(projectId),
    onSuccess: (_response, projectId) => {
      // Drop any pending goals autosave for the deleted project so a
      // debounced PATCH can't fire after deletion and 404 with a phantom
      // "Save failed" toast.
      goalsAutosaveCoordinator.cancel(projectId);
      // Invalidate cache for project deletion
      cacheInvalidation.afterDeleteProject(projectId);

      toast.undoable(t('projectDeleted'), {
        action: {
          label: tCommon('undo'),
          onClick: async () => {
            try {
              await projectsService.restore(projectId);
              queryClient.invalidateQueries({ queryKey: cacheKeys.projects.lists() });
            } catch (error) {
              toast.error(
                error instanceof Error
                  ? error.message
                  : t('failedToRestoreProject')
              );
            }
          },
        },
      });
    },
    // Note: WebSocket event handler also triggers invalidation
    // See: project.handlers.ts -> handleProjectDeleted
    // Having both is redundant but ensures cache consistency
  });
}

// Hook for enhancing project goals
export function useEnhanceGoals() {
  return useMutation({
    mutationKey: ['projects', 'enhance-goals'],
    mutationFn: ({ projectId, goals }: { projectId: string; goals: string }) =>
      projectsService.enhanceGoals(projectId, goals),
  });
}

// Hook for fetching project concepts with pagination
export function useProjectConcepts(
  projectId: string,
  params?: ProjectListParams
) {
  return useQuery({
    queryKey: cacheKeys.projects.concepts(projectId),
    queryFn: () => projectsService.listConcepts(projectId, params),
    enabled: !!projectId && params?.enabled !== false,
  });
}

// Hook for infinite scrolling projects (if needed in future)
export function useInfiniteProjects(
  params?: ProjectListParams & { pageSize?: number }
) {
  const pageSize = params?.pageSize || 20;

  return useInfiniteQuery({
    queryKey: cacheKeys.projects.list(params),
    queryFn: ({ pageParam = 1 }) =>
      projectsService.list({
        sort: params?.sort || '-created_at',
        filter: params?.filter,
        number: pageParam,
        size: pageSize,
      }),
    getNextPageParam: (lastPage, pages) => {
      const totalItems = lastPage.meta?.total || 0;
      const loadedItems = pages.length * pageSize;
      return loadedItems < totalItems ? pages.length + 1 : undefined;
    },
    initialPageParam: 1,
  });
}
